import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'gatsby';
import { HEM_COMMERCE_BASE_PATH } from 'gatsby-theme-hem-store/src/constants';
import { Button, Checkbox, Input, LoadingIndicator, transition } from 'hem-ui';
import Cookies from 'js-cookie';
import { Check } from 'lucide-react';
import React, { FormEvent, useState } from 'react';

import * as variantAvailabilityStyles from '@/components/VariantAvailability.module.scss';
import { getAudience } from '@/utils/getAudience';
import { getUserId } from '@/utils/getUserId';
import { sendGtmWaitingListSignupEvent } from '@/utils/sendGtmEvents';

import { Modal } from '../Modal';

type Props = {
  name: string;
  shopifyVariantId: string;
  sku: string;
};

enum Status {
  IDLE,
  LOADING,
  ERROR,
  SUCCESS,
}

export const BackInStock: React.FC<Props> = ({
  name,
  shopifyVariantId,
  sku,
}): JSX.Element => {
  const [acceptsMarketing, setAcceptsMarketing] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(Status.IDLE);

  const addToWaitlist = async (e: FormEvent) => {
    // Prevent the default form submission behavior
    e.preventDefault();

    // Set the status to loading
    setStatus(Status.LOADING);

    try {
      // Start the fetch request to Klaviyo
      const klaviyoPromise = fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${process.env.GATSBY_KLAVIYO_API_KEY}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2023-12-15',
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                    },
                  },
                },
                channels: ['EMAIL'],
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${shopifyVariantId}`,
                  },
                },
              },
            },
          }),
        },
      );

      // Initialize a resolved promise for the Hem Commerce request
      let hemCommercePromise = Promise.resolve({ ok: true, statusText: '' });

      // If the user accepts marketing, start the fetch request to Hem Commerce
      if (acceptsMarketing) {
        const tags = ['Back in stock'];
        const utmTags = Cookies.get('utmTags');
        if (utmTags !== '') {
          tags.push(utmTags);
        }

        // Overwrite the resolved promise with the actual fetch request
        hemCommercePromise = fetch(
          `${HEM_COMMERCE_BASE_PATH}/customer_mailing_list/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              audience: getAudience(),
              email: email,
              tags: tags.filter(Boolean),
              userid: getUserId(),
              profileid: Cookies.get('hem-klaviyo-profile-id'),
            }),
          },
        );
      }

      // Wait for both fetch requests to complete
      const [klaviyoResult, hemCommerceResult] = await Promise.all([
        klaviyoPromise,
        hemCommercePromise,
      ]);

      // If the Klaviyo request failed, throw an error
      if (!klaviyoResult.ok) {
        throw new Error(klaviyoResult.statusText);
      }

      // Send a signup event to GTM
      sendGtmWaitingListSignupEvent({ sku });

      // If the user accepts marketing and the Hem Commerce request failed, throw an error
      if (acceptsMarketing && !hemCommerceResult.ok) {
        throw new Error(hemCommerceResult.statusText);
      }

      // After a delay, set the status to success
      setTimeout(() => {
        setStatus(Status.SUCCESS);
      }, 1000);

      // After a longer delay, reset the email, acceptsMarketing, and status fields
      setTimeout(() => {
        setEmail('');
        setAcceptsMarketing(false);
        setStatus(Status.IDLE);
      }, 4000);
    } catch (error) {
      // If an error was thrown at any point in the try block, log the error and set the status to error
      console.error('Back in stock error:', error);
      setStatus(Status.ERROR);
    }
  };

  return (
    <>
      <Modal
        trigger={
          <Button className="w-full" color="black">
            Add me to the waitlist
          </Button>
        }
        title="Join the waitlist"
        content={
          <div className="text-black p-4 text-base">
            <p className="mb-4 text-pretty">
              Register your email address below to receive an email as soon as{' '}
              <span className="font-bold">{name}</span> becomes available again.
            </p>
            <form className="mb-4 space-y-2" onSubmit={addToWaitlist}>
              <Input
                placeholder="Your email address"
                value={email}
                type="email"
                required
                onChange={(event) => setEmail(event.target.value)}
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              />
              <Checkbox
                defaultChecked={true}
                checked={acceptsMarketing}
                label="I would like to sign up for Hem e-mail updates"
                onChange={() => setAcceptsMarketing(!acceptsMarketing)}
              />
              <Button
                className="w-full"
                type="submit"
                disabled={
                  status === Status.LOADING || status === Status.SUCCESS
                }
                color={status === Status.SUCCESS ? 'green' : undefined}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    className="flex items-center justify-center gap-2"
                    initial={{ translateY: -2, opacity: 0 }}
                    animate={{ translateY: 0, opacity: 1 }}
                    exit={{ translateY: 2, opacity: 0 }}
                    key={status}
                    {...transition}
                  >
                    {
                      {
                        [Status.LOADING]: (
                          <>
                            <LoadingIndicator />
                            Adding to waitlist…
                          </>
                        ),
                        [Status.SUCCESS]: (
                          <>
                            <Check size={20} className="text-white" />
                            Added to waitlist!
                          </>
                        ),
                        [Status.IDLE]: <>Add me to the waitlist</>,
                        [Status.ERROR]: <>Add me to the waitlist</>,
                      }[status]
                    }
                  </motion.div>
                </AnimatePresence>
              </Button>
            </form>
            {status === Status.ERROR && (
              <p className="text-error mb-4 text-center text-sm">
                Uh-oh! Something went wrong. Please try again later.
              </p>
            )}
            <p className="text-balance text-center text-sm text-black-600">
              By joining the waitlist you accept our{' '}
              <Link to="/terms-and-conditions/">Terms and conditions</Link> and{' '}
              <Link to="/privacy-policy">Privacy policy</Link>.
            </p>
          </div>
        }
      />
      <div className={[variantAvailabilityStyles.root, 'mt-2 h-5'].join(' ')}>
        <div
          className={[
            variantAvailabilityStyles.dot,
            variantAvailabilityStyles.red,
          ].join(' ')}
        />
        <p className="text-base text-black-600">Out of stock</p>
      </div>
    </>
  );
};
